import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Search, ChevronDown, ChevronRight, ExternalLink, Globe, Lock, Link2, Pencil, Trash2, Plus, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { CategoryDialog, LinkDialog } from "./LinkDialogs";

const host = (u) => { try { return new URL(u).hostname; } catch { return ""; } };

function Favicon({ url }) {
  const [bad, setBad] = useState(false);
  const h = host(url);
  if (bad || !h) return <Globe className="h-4 w-4 text-slate-400 shrink-0" />;
  return <img src={`https://www.google.com/s2/favicons?domain=${h}&sz=32`} alt="" referrerPolicy="no-referrer" onError={() => setBad(true)} className="h-4 w-4 rounded-sm shrink-0" />;
}

const Restricted = ({ v }) => (v?.division_ids?.length || v?.profile_ids?.length || v?.user_ids?.length)
  ? <span className="text-[10px] text-sky-300" title="Only selected divisions, access profiles or people can see this">selected audience</span> : null;

function LinkRow({ x, manage, onEdit, onDelete }) {
  return (
    <div className="group flex items-center gap-3 rounded-md px-2 py-2 hover:bg-white/5 transition-colors" data-testid={`link-row-${x.id}`}>
      <Favicon url={x.url} />
      <a href={x.url} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0" data-testid={`link-open-${x.id}`}>
        <div className="text-sm text-slate-100 truncate flex items-center gap-1.5">{x.title}{!x.is_active && <span className="text-[10px] text-amber-400 flex items-center gap-0.5"><EyeOff className="h-3 w-3" />inactive</span>}{manage && <Restricted v={x.visibility} />}</div>
        {x.description && <div className="text-[11px] text-slate-500 truncate">{x.description}</div>}
      </a>
      {manage && <button onClick={() => onEdit(x)} className="opacity-60 hover:opacity-100" data-testid={`link-edit-${x.id}`}><Pencil className="h-3.5 w-3.5 text-slate-300" /></button>}
      {manage && <button onClick={() => onDelete(x)} className="opacity-60 hover:opacity-100" data-testid={`link-delete-${x.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>}
      <ExternalLink className="h-3.5 w-3.5 text-[#C2A462]/70 shrink-0" />
    </div>
  );
}

function CategoryBlock({ c, open, toggle, manage, editable, onAddLink, onEditCat, onDeleteCat, onEditLink, onDeleteLink }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/20" data-testid={`link-cat-${c.id}`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button onClick={toggle} className="flex-1 flex items-center gap-2 text-left" data-testid={`link-cat-toggle-${c.id}`}>
          {open ? <ChevronDown className="h-4 w-4 text-[#C2A462]" /> : <ChevronRight className="h-4 w-4 text-[#C2A462]" />}
          <span className="text-sm font-semibold text-slate-100">{c.name}</span>
          {c.scope === "personal" && <Lock className="h-3 w-3 text-slate-500" />}
          {!c.is_active && <span className="text-[10px] text-amber-400">inactive</span>}
          {manage && c.scope === "org" && <Restricted v={c.visibility} />}
          <span className="text-[11px] text-slate-500">{c.links.length}</span>
        </button>
        {manage && editable && <>
          <button onClick={() => onAddLink(c)} title="Add link" data-testid={`link-cat-add-${c.id}`}><Plus className="h-4 w-4 text-[#C2A462]" /></button>
          <button onClick={() => onEditCat(c)} title="Edit category" data-testid={`link-cat-edit-${c.id}`}><Pencil className="h-3.5 w-3.5 text-slate-300" /></button>
          <button onClick={() => onDeleteCat(c)} title="Delete category" data-testid={`link-cat-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
        </>}
      </div>
      {open && <div className="px-1 pb-2">
        {c.description && <div className="px-3 pb-1 text-[11px] text-slate-500">{c.description}</div>}
        {c.links.map((x) => <LinkRow key={x.id} x={x} manage={manage && editable} onEdit={onEditLink} onDelete={onDeleteLink} />)}
        {!c.links.length && <div className="px-3 py-2 text-xs text-slate-500">No links yet.</div>}
      </div>}
    </div>
  );
}

export function LinksDirectory({ compact = false, manage = false }) {
  const [dir, setDir] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState({});
  const [cat, setCat] = useState(null);
  const [link, setLink] = useState(null);
  const [expanded, setExpandedRaw] = useState(() => !compact || localStorage.getItem("bos_links_widget") === "open");
  const setExpanded = (v) => { localStorage.setItem("bos_links_widget", v ? "open" : "closed"); setExpandedRaw(v); };
  const load = () => api.get(`/links/directory?manage=${manage}`).then(({ data }) => { setDir(data); setErr(null); }).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, [manage]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(() => api.get(`/links/search`, { params: { q } }).then(({ data }) => setResults(data.results)).catch(() => setResults([])), 200);
    return () => clearTimeout(t);
  }, [q, dir]);
  const cats = useMemo(() => dir?.categories || [], [dir]);
  const editable = (c) => c.scope === "personal" || dir?.can_manage;
  const delCat = async (c) => {
    try { await api.delete(`/links/categories/${c.id}`); }
    catch (e) {
      if (e.response?.status !== 409) return toast.error(errMsg(e));
      if (!window.confirm(`${e.response.data.detail}\nDelete "${c.name}" and its links?`)) return;
      await api.delete(`/links/categories/${c.id}?with_links=true`);
    }
    toast.success("Category deleted"); load();
  };
  const delLink = async (x) => { if (!window.confirm(`Delete "${x.title}"?`)) return; try { await api.delete(`/links/items/${x.id}`); toast.success("Link deleted"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const props = (c) => ({ manage, editable: editable(c), onAddLink: (k) => setLink({ category_id: k.id, _scope: k.scope }), onEditCat: setCat, onDeleteCat: delCat, onEditLink: (x) => setLink({ ...x, _scope: c.scope }), onDeleteLink: delLink });
  const list = results ? results.map((r) => ({ ...cats.find((c) => c.id === r.id), links: r.links })) : cats;
  const org = list.filter((c) => c.scope === "org"), mine = list.filter((c) => c.scope === "personal");
  const section = (title, arr, scope) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between"><div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{title}</div>
        {manage && (scope === "personal" || dir?.can_manage) && <div className="flex items-center gap-3">
          <button onClick={() => setLink({ _scope: scope })} className="text-xs font-semibold rounded-full bg-[#C2A462] text-[#0B1220] px-3 py-1 hover:bg-[#d4b876] transition-colors flex items-center gap-1" data-testid={`link-add-${scope}`}><Plus className="h-3 w-3" />Add link</button>
          <button onClick={() => setCat({ scope })} className="text-xs text-[#C2A462] hover:underline flex items-center gap-1" data-testid={`link-new-cat-${scope}`}><Plus className="h-3 w-3" />New category</button>
        </div>}</div>
      {arr.map((c) => <CategoryBlock key={c.id} c={c} open={!!results || !!open[c.id]} toggle={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))} {...props(c)} />)}
      {!arr.length && !results && <div className="text-xs text-slate-500">{scope === "personal" ? "Your private links appear here. Only you can see them." : "No organisation links yet."}</div>}
    </div>
  );
  return (
    <div className={`rounded-xl border border-white/10 bg-[#101828] ${compact ? "p-4" : "p-5"}`} data-testid={compact ? "important-links-widget" : "important-links-directory"}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2"><Link2 className={`${compact ? "h-4 w-4" : "h-5 w-5"} text-[#C2A462]`} /><div><div className={`${compact ? "text-sm" : "text-base"} font-bold text-slate-100`}>Important Links</div>{!compact && <div className="text-xs text-slate-400">Your business resource centre</div>}</div></div>
        {compact && <div className="flex items-center gap-3">
          {expanded && <button onClick={() => setLink({ _scope: "personal" })} className="text-[11px] font-semibold rounded-full border border-[#C2A462]/60 text-[#EBD6A0] px-2.5 py-0.5 hover:bg-[#C2A462]/15 transition-colors flex items-center gap-1" data-testid="links-widget-add"><Plus className="h-3 w-3" />Add link</button>}
          {expanded && <RouterLink to="/app/links" className="text-[11px] text-[#C2A462] hover:underline" data-testid="links-view-all">View all</RouterLink>}
          <button onClick={() => setExpanded(!expanded)} aria-expanded={expanded} className="text-[11px] text-slate-300 hover:text-[#EBD6A0]" data-testid="links-widget-toggle">{expanded ? "Close" : `Expand${dir ? ` (${cats.reduce((n, c) => n + (c.links?.length || 0), 0)})` : ""}`}</button>
        </div>}
      </div>
      {expanded && <>
      <div className={`relative ${compact ? "mt-3" : "mt-4"}`}>
        <Search className={`absolute left-3 ${compact ? "top-2" : "top-2.5"} h-4 w-4 text-slate-500`} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search websites, portals or resources..." data-testid="links-search"
          className={`${compact ? "h-8" : "h-10"} w-full rounded-md border border-white/10 bg-black/30 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#C2A462]/60 transition-colors`} />
      </div>
      {err && <div className="mt-3 text-sm text-red-400">{err}</div>}
      {!dir && !err && <div className="mt-4 text-xs text-slate-500">Loading…</div>}
      {dir && <div className={compact ? "mt-3 space-y-3 max-h-[300px] overflow-y-auto pr-1" : "mt-4 space-y-5"}>
        {results && !results.length && <div className="text-sm text-slate-400" data-testid="links-no-results">No links match "{q}".</div>}
        {(org.length > 0 || !results) && section("Organisation links", org, "org")}
        {(mine.length > 0 || !results) && section("My links", mine, "personal")}
      </div>}
      </>}
      {cat && <CategoryDialog initial={cat} onClose={() => setCat(null)} onSaved={load} />}
      {link && <LinkDialog initial={link} categories={cats.filter((c) => c.scope === link._scope)} onClose={() => setLink(null)} onSaved={load} />}
    </div>
  );
}
